import { css } from 'aphrodite';
import * as React from 'react';
import { useMetaGlobalConnectionVerifyQuery } from '../../../../queries/Admin/Meta';
import { Button } from '../../../../web/components-v2/Button';
import { Pill } from '../../../../web/components-v2/Pill';
import { MetaUsersGroupIcon } from '../icons';
import { formatUtcTimestamp } from '../utils';
import { styleSheet } from './styles';

type HealthState = 'healthy' | 'notConfigured' | 'verifyFailed' | 'loading';

export const MetaHealthCard: React.FC = () => {
	const { data, isPending, isFetching, isError, refetch } = useMetaGlobalConnectionVerifyQuery();

	const state: HealthState = (() => {
		if (isPending) return 'loading';
		// Surface network/5xx failures as a verification failure pill — otherwise
		// the skeleton would stay up forever (staleTime: Infinity, no auto-retries).
		if (isError || !data) return 'verifyFailed';
		if (!data.tokenConfigured) return 'notConfigured';
		if (!data.verificationSucceeded) return 'verifyFailed';
		return 'healthy';
	})();

	const { label: statusLabel, pillColor } = STATE_PRESENTATION[state];

	return (
		<section className={css(styleSheet.card)} aria-label='Meta System User connection health'>
			<div className={css(styleSheet.headerRow)}>
				<div className={css(styleSheet.titleRow)}>
					<MetaUsersGroupIcon className={css(styleSheet.titleIcon)} />
					<h2 className={css(styleSheet.title)}>Meta System User Connection</h2>
				</div>
				<div className={css(styleSheet.statusRow)}>
					{state === 'healthy' && data?.checkedAt ? (
						<span className={css(styleSheet.verifiedAt)}>Last verified: {formatUtcTimestamp(data.checkedAt)}</span>
					) : null}
					{pillColor ? <Pill variant='label' color={pillColor} label={statusLabel} /> : null}
				</div>
			</div>

			<HealthBody state={state} data={data} />

			<div className={css(styleSheet.actions)}>
				<Button
					variant='secondary'
					onClick={() => refetch()}
					isLoading={isFetching}
					disabled={isFetching || state === 'notConfigured'}
				>
					Re-verify Now
				</Button>
			</div>
		</section>
	);
};

const HealthBody: React.FC<{
	state: HealthState;
	data: ReturnType<typeof useMetaGlobalConnectionVerifyQuery>['data'];
}> = ({ state, data }) => {
	if (state === 'loading') {
		return (
			<div className={css(styleSheet.body)} aria-busy='true'>
				<div className={css(styleSheet.skeletonBar, styleSheet.skeletonBarWide)} />
				<div className={css(styleSheet.skeletonBar, styleSheet.skeletonBarNarrow)} />
			</div>
		);
	}
	if (state === 'notConfigured') {
		return (
			<p className={css(styleSheet.helpText)}>
				Set <code>Ads:Meta:SystemUser:Token</code> in AWS Secrets Manager and restart pods. See the System User runbook
				for setup steps.
			</p>
		);
	}
	if (state === 'verifyFailed') {
		return (
			<div className={css(styleSheet.body)}>
				<p className={css(styleSheet.helpText)}>The token is configured but Meta rejected the verification request.</p>
				{data?.error ? <pre className={css(styleSheet.errorText)}>{data.error}</pre> : null}
			</div>
		);
	}
	// healthy
	return (
		<div className={css(styleSheet.body)}>
			{data?.systemUserName ? (
				<div className={css(styleSheet.bodyRow)}>
					<span className={css(styleSheet.bodyLabel)}>System User:</span>
					<span className={css(styleSheet.bodyValue)}>{data.systemUserName}</span>
				</div>
			) : null}
			{data?.systemUserId ? (
				<div className={css(styleSheet.bodyRow)}>
					<span className={css(styleSheet.bodyLabel)}>ID:</span>
					<span className={css(styleSheet.bodyId)}>{data.systemUserId}</span>
				</div>
			) : null}
		</div>
	);
};

type PillColor = 'green' | 'gray' | 'red';
const STATE_PRESENTATION: Record<HealthState, { label: string; pillColor: PillColor | null }> = {
	healthy: { label: 'Connected', pillColor: 'green' },
	notConfigured: { label: 'Not configured', pillColor: 'gray' },
	verifyFailed: { label: 'Verification failed', pillColor: 'red' },
	loading: { label: 'Checking…', pillColor: null },
};
